/**
 * Daily Operating Package — Sprint 10 · 2026-08-13.
 *
 * PO directive: a formal, print-ready daily operating record that
 * consumes authoritative backend data (never transient browser state).
 * The operator previews the package for any business date, then hits
 * *Print / Save as PDF* which invokes the browser's print dialog with
 * print-tuned CSS.
 *
 * This is a **reporting/output feature only** — nothing on this page
 * mutates data, sends notifications, or triggers approvals.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import "./DailyPackage.print.css";


const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const fmtL = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} L`;
};
const fmtGL = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${abs} L`;
};
const fmtPct = (v) => (v === null || v === undefined) ? "—" : `${Number(v).toFixed(2)}%`;
const fmtPrice = (v) => (v === null || v === undefined || v === "") ? "—"
  : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;

/**
 * Auditor-facing formatters · Sprint 10 · 2026-08-13.
 *
 * The Daily Package is an operational + audit document — raw Decimal
 * strings ("1.150814536021093816449774602") must never reach the page.
 * All formatters clamp precision at the *display boundary* only; the
 * authoritative backend Decimal math is untouched.
 */
// Whole litres, comma-grouped, unit-suffixed. e.g. "70,542 L".
const fmtLitres = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 })} L`;
};
// Money — currency with thousands separators and 2 decimals. e.g. "$81,180.76".
const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Unit fuel cost — always 4 decimals with $/L suffix. e.g. "$1.1508/L".
const fmtPriceL = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}/L`;
};


export default function DailyPackage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialDate = params.get("date") || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (targetDate) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/daily-package", { params: { date: targetDate } });
      setData(d);
    } catch (e) {
      toast.error("Could not load Daily Package.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [load, date]);

  const onDateChange = (v) => {
    setDate(v);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("date", v);
      return next;
    }, { replace: true });
  };

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-slate-100 dp-root">
      {/* Screen-only controls · hidden in print via .dp-controls */}
      <div className="dp-controls sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-5xl px-6 py-3 flex flex-wrap items-center gap-3">
          <button
            onClick={() => nav("/start-the-day")}
            data-testid="daily-package-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Back to Start the Day
          </button>
          <div className="flex-1" />
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            Business date
            <input
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              data-testid="daily-package-date-picker"
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            />
          </label>
          <button
            onClick={() => load(date)}
            data-testid="daily-package-refresh"
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
          <button
            onClick={onPrint}
            disabled={loading || !data}
            data-testid="daily-package-print"
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
          >
            <Printer size={14} weight="bold" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500" data-testid="daily-package-loading">Loading package…</div>
      ) : !data ? (
        <div className="p-10 text-center text-rose-700" data-testid="daily-package-error">Could not load the package.</div>
      ) : (
        <article className="dp-pages mx-auto max-w-5xl bg-white shadow-sm my-6" data-testid="daily-package-doc">
          <CoverPage data={data} />
          <RackPricePage data={data} />
          <TankInventoryPage data={data} />
          <DailyReadingsPage data={data} />
          <FifoReportPage data={data} />
          <ReconciliationPage data={data} />
          <DeliverySummaryPage data={data} />
          <AuditSnapshotPage data={data} />
        </article>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------
// Page 1 · Cover / Executive Summary
// ---------------------------------------------------------------------
function CoverPage({ data }) {
  const meta = data.meta || {};
  const c = data.cover || {};
  const rp = c.rack_price || {};
  const dn = c.driver_notification || {};
  const r  = c.reconciliation || {};
  const approved = r.approved;

  return (
    <PrintPage number={1} businessDate={meta.business_date} totalPages={8}>
      <PackageHeader meta={meta} />

      <div className="mt-8 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Daily Operating Package</div>
          <h1 className="mt-1 font-display text-4xl font-semibold text-slate-900">
            {fmtDate(meta.business_date)}
          </h1>
        </div>
        <ApprovalStamp approved={approved} overallStatus={r.overall_status}
                       approvedBy={r.approved_by} approvedAt={r.approved_at} />
      </div>

      <hr className="my-6 border-slate-200" />

      <section data-testid="dp-cover-status-grid">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3">
          Workflow Status
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <StatusBox label="Start-the-Day"
                     value={c.overall_status || "—"} />
          <StatusBox label="Price Notification"
                     value={c.steps?.price_notification?.status || "—"}
                     sub={c.steps?.price_notification?.completed_by || null}
                     subAt={c.steps?.price_notification?.completed_at} />
          <StatusBox label="Daily Readings"
                     value={c.steps?.daily_readings?.status || "—"}
                     sub={c.steps?.daily_readings?.completed_by || null}
                     subAt={c.steps?.daily_readings?.completed_at} />
          <StatusBox label="Tank Inventory"
                     value={c.steps?.tank_inventory?.status || "—"}
                     sub={c.steps?.tank_inventory?.completed_by || null}
                     subAt={c.steps?.tank_inventory?.completed_at} />
          <StatusBox label="Rack Price Approval"
                     value={rp.present ? (rp.status || "—") : "not_uploaded"}
                     sub={rp.approved_by}
                     subAt={rp.approved_at} />
          <StatusBox label="Driver Notification"
                     value={dn.sent ? "SENT" : "NOT_SENT"}
                     sub={dn.sent && dn.recipient_count ? `${dn.recipient_count} recipients` : null}
                     subAt={dn.sent_at} />
          <StatusBox label="Inventory Review"
                     value={c.steps?.reconciliation?.status || "—"}
                     sub={r.approved_by}
                     subAt={r.approved_at}
                     span />
        </div>
      </section>

      <section className="mt-8" data-testid="dp-cover-exceptions">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3">
          Key Exceptions Requiring Attention
        </h2>
        {(!c.exceptions || c.exceptions.length === 0) ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            No exceptions — every product within tolerance.
          </div>
        ) : (
          <ul className="space-y-1.5 text-sm text-slate-800">
            {c.exceptions.map((ex) => (
              <li key={ex.product_code} className="flex items-center justify-between border-b border-slate-100 py-1.5">
                <span>
                  <b>{ex.product_label}</b>
                  <span className="ml-2 text-xs uppercase tracking-wider text-amber-800">
                    {ex.status === "RED" ? "Escalate" : "Review"}
                  </span>
                </span>
                <span className="font-mono text-sm">
                  {fmtGL(ex.diff_adjusted)} · {fmtPct(ex.variance_pct)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-8" data-testid="dp-cover-meta">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3">
          Package Meta
        </h2>
        <dl className="grid grid-cols-2 gap-y-1.5 text-xs text-slate-700">
          <MetaItem label="Business date"  value={fmtDate(meta.business_date)} />
          <MetaItem label="Prepared by"    value={meta.prepared_by_name || meta.prepared_by || "—"} />
          <MetaItem label="Generated at"   value={fmtDateTime(meta.generated_at)} />
          <MetaItem label="Organisation"   value={meta.org_name || "—"} />
        </dl>
      </section>
    </PrintPage>
  );
}

function ApprovalStamp({ approved, overallStatus, approvedBy, approvedAt }) {
  const cls = approved
    ? "border-emerald-500 text-emerald-800 bg-emerald-50"
    : "border-amber-500 text-amber-900 bg-amber-50";
  return (
    <div className={`rounded-md border-2 ${cls} px-4 py-2 text-right`}
         data-testid={`dp-approval-stamp-${approved ? "approved" : "pending"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">Reconciliation</div>
      <div className="text-lg font-bold">
        {approved ? "APPROVED" : "PENDING APPROVAL"}
      </div>
      {approved ? (
        <div className="text-[10px] leading-tight mt-0.5">
          {approvedBy || "—"}
          <br />
          {fmtDateTime(approvedAt)}
          <br />
          Overall: <b>{overallStatus || "—"}</b>
        </div>
      ) : (
        <div className="text-[10px] leading-tight mt-0.5">
          Overall: <b>{overallStatus || "—"}</b>
        </div>
      )}
    </div>
  );
}

function StatusBox({ label, value, sub, subAt, span }) {
  const v = String(value || "").toUpperCase();
  const tone = v === "COMPLETE" || v === "APPROVED" || v === "SENT"
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : v === "IN_PROGRESS" || v === "PARSED"
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`rounded border ${tone} px-3 py-2 ${span ? "col-span-2" : ""}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider">{label}</div>
      <div className="mt-0.5 text-sm font-semibold">{v.replace(/_/g, " ")}</div>
      {(sub || subAt) ? (
        <div className="text-[10px] text-slate-600 mt-0.5">
          {sub || ""}{sub && subAt ? " · " : ""}{fmtDateTime(subAt)}
        </div>
      ) : null}
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-mono text-slate-800">{value}</dd>
    </>
  );
}


// ---------------------------------------------------------------------
// Page 2 · Rack Price Report
// ---------------------------------------------------------------------
function RackPricePage({ data }) {
  const rp = data.rack_prices || { rows: [], count: 0 };
  const cover = data.cover?.rack_price || {};
  const dn = data.cover?.driver_notification || {};
  return (
    <PrintPage number={2} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader title="Rack Price Report" subtitle="Approved supplier price notification for the business date" />

      {!cover.present ? (
        <EmptyState message="No price notification uploaded for this business date." />
      ) : (
        <>
          <table className="w-full text-sm mt-3" data-testid="dp-rack-price-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">Supplier</th>
                <th className="py-2 pr-2">Terminal</th>
                <th className="py-2 pr-2">Effective</th>
                <th className="py-2 pr-2">Product</th>
                <th className="py-2 pr-2 text-right">Previous</th>
                <th className="py-2 pr-2 text-right">New</th>
                <th className="py-2 pr-2 text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {rp.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-2">{r.supplier || "—"}</td>
                  <td className="py-1.5 pr-2 text-xs text-slate-600">{r.terminal || "—"}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">{r.effective_date || "—"}</td>
                  <td className="py-1.5 pr-2">{r.product_label || r.product_code}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtPrice(r.previous_price)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold">{fmtPrice(r.new_price)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtPrice(r.price_change)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 grid grid-cols-2 gap-6">
            <InfoBlock title="Approval">
              <InfoLine label="Status"      value={(cover.status || "—").toUpperCase()} />
              <InfoLine label="Approved by" value={cover.approved_by || "—"} />
              <InfoLine label="Approved at" value={fmtDateTime(cover.approved_at)} />
            </InfoBlock>
            <InfoBlock title="Driver Notification">
              <InfoLine label="Status"     value={dn.sent ? "SENT" : "NOT SENT"} />
              <InfoLine label="Sent by"    value={dn.sent_by || "—"} />
              <InfoLine label="Recipients" value={dn.recipient_count ?? "—"} />
              <InfoLine label="Sent at"    value={fmtDateTime(dn.sent_at)} />
            </InfoBlock>
          </div>
        </>
      )}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Page 3 · Tank Inventory Report
// ---------------------------------------------------------------------
function TankInventoryPage({ data }) {
  const ti = data.tank_inventory || { bulk_plant: [], trucks: [], totals_by_product: [] };
  // Map RTS by product from reconciliation (RTS lives on recon, tank
  // inventory is meter-based). Show a per-product summary block.
  const rtsByProduct = {};
  const dvByProduct = {};
  const thrByProduct = {};
  for (const it of (data.reconciliation?.items || [])) {
    rtsByProduct[it.product_code] = it.rts;
    dvByProduct[it.product_code] = it.deliveries;
    thrByProduct[it.product_code] = it.throughput_yesterday;
  }
  return (
    <PrintPage number={3} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader title="Tank Inventory Report" subtitle="Bulk-plant tanks and truck compartments" />
      {!ti.captured ? (
        <EmptyState message="No tank inventory recorded for this business date." />
      ) : (
        <>
          <table className="w-full text-sm mt-3" data-testid="dp-tank-inventory-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">Tank</th>
                <th className="py-2 pr-2">Product</th>
                <th className="py-2 pr-2">Location</th>
                <th className="py-2 pr-2 text-right">Capacity</th>
                <th className="py-2 pr-2 text-right">Opening</th>
                <th className="py-2 pr-2 text-right">Closing Physical</th>
              </tr>
            </thead>
            <tbody>
              {ti.bulk_plant.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-2 font-mono">{row.tank_number || row.tank_id}</td>
                  <td className="py-1.5 pr-2">{row.product?.replace(/_/g, " ") || "—"}</td>
                  <td className="py-1.5 pr-2 text-xs text-slate-600">{row.location || "—"}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.capacity_l)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.opening_balance)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold">{fmtL(row.closing_physical)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-700">
            Product-Level Totals · Movement Summary
          </h3>
          <div className="mt-1 text-[10px] italic text-slate-500">
            Opening + Deliveries + RTS &minus; Throughput = Closing.
          </div>
          <table className="w-full text-sm mt-2" data-testid="dp-tank-inventory-totals">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">Product</th>
                <th className="py-2 pr-2 text-right">Opening</th>
                <th className="py-2 pr-2 text-right">Deliveries</th>
                <th className="py-2 pr-2 text-right">RTS</th>
                <th className="py-2 pr-2 text-right">Throughput</th>
                <th className="py-2 pr-2 text-right">Closing</th>
              </tr>
            </thead>
            <tbody>
              {(ti.totals_by_product || []).map((row, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`dp-tank-inventory-totals-${row.product}`}>
                  <td className="py-1.5 pr-2">{row.product_label || row.product?.replace(/_/g, " ") || "—"}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtLitres(row.opening)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtLitres(row.deliveries)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtLitres(row.rts)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtLitres(row.throughput)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold">{fmtLitres(row.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {ti.trucks?.length > 0 ? (
            <>
              <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-700">
                Truck Compartments
              </h3>
              <table className="w-full text-sm mt-2" data-testid="dp-tank-inventory-trucks">
                <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
                  <tr>
                    <th className="py-2 pr-2">Truck</th>
                    <th className="py-2 pr-2">Driver</th>
                    <th className="py-2 pr-2 text-right">Regular</th>
                    <th className="py-2 pr-2 text-right">Premium</th>
                    <th className="py-2 pr-2 text-right">Clear Diesel</th>
                    <th className="py-2 pr-2 text-right">Dyed Diesel</th>
                  </tr>
                </thead>
                <tbody>
                  {ti.trucks.map((row, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 pr-2 font-mono">{row.truck_number || row.truck_id}</td>
                      <td className="py-1.5 pr-2 text-xs text-slate-600">{row.driver_name || "—"}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.regular)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.premium)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.clear_diesel)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.dyed_diesel)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      )}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Page 4 · Daily Readings Report
// ---------------------------------------------------------------------
function DailyReadingsPage({ data }) {
  const dr = data.daily_readings || { cardlock_pumps: [], loading_rack: [] };
  return (
    <PrintPage number={4} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader title="Daily Readings Report" subtitle="Cardlock pump meters and loading-rack lane totals" />
      {!dr.captured ? (
        <EmptyState message="No daily readings recorded for this business date." />
      ) : (
        <>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            Cardlock Pumps
          </h3>
          <table className="w-full text-sm mt-2" data-testid="dp-daily-readings-cardlock">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">Pump</th>
                <th className="py-2 pr-2">Product</th>
                <th className="py-2 pr-2 text-right">Opening</th>
                <th className="py-2 pr-2 text-right">Closing</th>
                <th className="py-2 pr-2 text-right">Throughput (L)</th>
              </tr>
            </thead>
            <tbody>
              {dr.cardlock_pumps.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-2 font-mono">{row.pump} · {row.label}</td>
                  <td className="py-1.5 pr-2">{row.product?.replace(/_/g, " ") || "—"}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.open)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.close)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold">{fmtL(row.throughput)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-700">
            Loading Rack
          </h3>
          <table className="w-full text-sm mt-2" data-testid="dp-daily-readings-rack">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">Product</th>
                <th className="py-2 pr-2 text-right">Opening</th>
                <th className="py-2 pr-2 text-right">Closing</th>
                <th className="py-2 pr-2 text-right">Throughput (L)</th>
              </tr>
            </thead>
            <tbody>
              {dr.loading_rack.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-2">{row.product?.replace(/_/g, " ") || "—"}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.open)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(row.close)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-semibold">{fmtL(row.throughput)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {dr.skimming_inspection ? (
            <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="font-semibold uppercase tracking-wider text-slate-600">Skimming Inspection</div>
              <div className="mt-1">
                Completed: <b>{dr.skimming_inspection.completed ? "YES" : "NO"}</b>
                {dr.skimming_inspection.recorded_by ? ` · ${dr.skimming_inspection.recorded_by}` : ""}
                {dr.skimming_inspection.recorded_at ? ` · ${fmtDateTime(dr.skimming_inspection.recorded_at)}` : ""}
              </div>
              {dr.skimming_inspection.notes ? (
                <div className="mt-1 italic text-slate-600">"{dr.skimming_inspection.notes}"</div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Page 5 · Daily Inventory Review (Reconciliation)
// ---------------------------------------------------------------------
function ReconciliationPage({ data }) {
  const rc = data.reconciliation || {};
  const items = rc.items || [];
  const completion = rc.completion || null;
  const th = rc.thresholds || {};
  return (
    <PrintPage number={6} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader title="Daily Inventory Review"
                     subtitle="Book vs Physical · Adjusted for Return-to-Storage" />

      <table className="w-full text-sm mt-3" data-testid="dp-recon-table">
        <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
          <tr>
            <th className="py-2 pr-2">Product</th>
            <th className="py-2 pr-2 text-right">Book</th>
            <th className="py-2 pr-2 text-right">RTS</th>
            <th className="py-2 pr-2 text-right">Adjusted Book</th>
            <th className="py-2 pr-2 text-right">Physical</th>
            <th className="py-2 pr-2 text-right">Diff (L)</th>
            <th className="py-2 pr-2 text-right">Var %</th>
            <th className="py-2 pr-2 text-right">Tol.</th>
            <th className="py-2 pr-2 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const statusText = it.status === "GREEN" ? "Pass"
              : it.status === "YELLOW" ? "Review"
              : it.status === "RED" ? "Escalate" : "—";
            const cls = it.status === "GREEN"
              ? "text-emerald-800 bg-emerald-50"
              : it.status === "YELLOW"
              ? "text-amber-900 bg-amber-50"
              : it.status === "RED"
              ? "text-rose-900 bg-rose-50"
              : "text-slate-700 bg-slate-50";
            return (
              <tr key={it.product_code} className="border-b border-slate-100"
                  data-testid={`dp-recon-row-${it.product_code}`}>
                <td className="py-1.5 pr-2 font-medium">{it.product_label}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtL(it.book_inventory_raw ?? it.book_inventory)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{it.rts != null ? fmtL(it.rts) : "—"}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtL(it.book_inventory_adjusted)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtL(it.physical_inventory)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtGL(it.diff_adjusted)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtPct(it.variance_pct)}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-slate-500">≤ {th.green_max_pct}%</td>
                <td className="py-1.5 pr-2 text-center">
                  <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
                    {statusText}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Per-row RTS breakdown when applicable — makes the adjusted
          math transparent on the printed page. */}
      {items.some((it) => it.rts != null && Number(it.rts) > 0) ? (
        <div className="mt-3 text-[11px] text-slate-600" data-testid="dp-recon-rts-breakdown">
          <div className="font-semibold text-slate-700 mb-1">RTS Adjustment Breakdown</div>
          <ul className="space-y-0.5">
            {items.filter((it) => it.rts != null && Number(it.rts) > 0).map((it) => (
              <li key={it.product_code} className="font-mono">
                {it.product_label}: Raw {fmtGL(it.diff_raw)} · RTS {fmtL(it.rts)} · Adjusted {fmtGL(it.diff_adjusted)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Overall status + approval block */}
      <div className="mt-6 grid grid-cols-2 gap-6">
        <InfoBlock title="Overall Status">
          <InfoLine label="Result" value={rc.overall_status || "—"} />
          <InfoLine label="Missing inputs" value={rc.has_missing_inputs ? "Yes" : "No"} />
        </InfoBlock>
        <InfoBlock title={completion ? "Approval" : "Pending Approval"}>
          {completion ? (
            <>
              <InfoLine label="Approved by" value={completion.acknowledged_by_name || completion.acknowledged_by} />
              <InfoLine label="Approved at" value={fmtDateTime(completion.acknowledged_at)} />
              <InfoLine label="Overall stamp" value={completion.overall_status} />
            </>
          ) : (
            <div className="text-xs text-amber-900">This package was generated before the operator approved the reconciliation.</div>
          )}
        </InfoBlock>
      </div>

      {completion?.investigation_notes ? (
        <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
             data-testid="dp-recon-investigation-notes">
          <div className="font-semibold uppercase tracking-wider text-slate-600">Investigation Notes</div>
          <div className="mt-1 italic text-slate-700">"{completion.investigation_notes}"</div>
        </div>
      ) : null}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Page 6 · Delivery Summary
// ---------------------------------------------------------------------
function DeliverySummaryPage({ data }) {
  const ds = data.delivery_summary || {};
  const approved = ds.approved || { rows: [], count: 0, total_litres: "0" };
  const pending  = ds.pending   || { invoices: [], count: 0 };
  const exception = ds.exception || { invoices: [], count: 0 };
  return (
    <PrintPage number={7} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader title="Delivery Summary"
                     subtitle="Supplier deliveries feeding today's Book Inventory" />

      {approved.rows.length === 0 ? (
        <EmptyState message="No approved supplier deliveries recorded for this business date." />
      ) : (
        <table className="w-full text-sm mt-3" data-testid="dp-delivery-approved">
          <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
            <tr>
              <th className="py-2 pr-2">Supplier</th>
              <th className="py-2 pr-2">Delivery Date</th>
              <th className="py-2 pr-2">Product</th>
              <th className="py-2 pr-2">Invoice</th>
              <th className="py-2 pr-2">State</th>
              <th className="py-2 pr-2 text-right">Litres</th>
            </tr>
          </thead>
          <tbody>
            {approved.rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5 pr-2">{r.supplier || "—"}</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{r.delivery_date || "—"}</td>
                <td className="py-1.5 pr-2">{r.product_label}</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{r.invoice_number || "—"}</td>
                <td className="py-1.5 pr-2 text-xs text-slate-600">{(r.business_state || "").replace(/_/g, " ")}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{fmtL(r.litres)}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-300 font-semibold bg-slate-50">
              <td className="py-1.5 pr-2" colSpan={5}>Total ({approved.count} approved deliveries)</td>
              <td className="py-1.5 pr-2 text-right font-mono">{fmtL(approved.total_litres)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {(pending.count > 0 || exception.count > 0) ? (
        <>
          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-700">
            Excluded from Book Inventory
          </h3>
          <p className="text-[11px] text-slate-500 italic">
            These invoices touched the business date but were not in an approved state at the time of package generation.
          </p>
          <table className="w-full text-sm mt-2" data-testid="dp-delivery-excluded">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
              <tr>
                <th className="py-2 pr-2">State</th>
                <th className="py-2 pr-2">Supplier</th>
                <th className="py-2 pr-2">Invoice</th>
                <th className="py-2 pr-2">Delivery Date</th>
                <th className="py-2 pr-2 text-right">Litres</th>
              </tr>
            </thead>
            <tbody>
              {[...pending.invoices.map((inv) => ({ ...inv, _bucket: "pending" })),
                ...exception.invoices.map((inv) => ({ ...inv, _bucket: "exception" }))].map((inv, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-2 text-xs uppercase tracking-wider text-slate-600">{(inv.business_state || inv._bucket).replace(/_/g, " ")}</td>
                  <td className="py-1.5 pr-2">{inv.supplier || "—"}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">{inv.invoice_number || "—"}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">{inv.delivery_date || "—"}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtL(inv.litres)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Shared print primitives
// ---------------------------------------------------------------------
function PrintPage({ number, businessDate, totalPages, children }) {
  return (
    <section className="dp-page" data-testid={`dp-page-${number}`}>
      {children}
      <footer className="dp-footer">
        <div>Coolman Fuels Ltd.</div>
        <div>Business Date · <span className="font-mono">{businessDate || "—"}</span></div>
        <div>Page <span className="dp-page-num">{number}</span> of {totalPages}</div>
      </footer>
    </section>
  );
}

function PackageHeader({ meta }) {
  return (
    <header className="dp-page-header flex items-center justify-between border-b border-slate-300 pb-2">
      <div>
        <div className="font-display text-base font-semibold text-slate-900">{meta?.org_name || "Coolman Fuels Ltd."}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Daily Operating Package</div>
      </div>
      <div className="text-right text-[10px] text-slate-500 leading-tight">
        <div className="font-mono">{meta?.business_date}</div>
        <div>Generated {fmtDateTime(meta?.generated_at)}</div>
      </div>
    </header>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mt-6">
      <h2 className="font-display text-2xl font-semibold text-slate-900">{title}</h2>
      {subtitle ? <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p> : null}
    </div>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">{title}</div>
      <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
        {children}
      </dl>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-mono text-slate-800">{value ?? "—"}</dd>
    </>
  );
}

function EmptyState({ message }) {
  return (
    <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500 italic">
      {message}
    </div>
  );
}


// ---------------------------------------------------------------------
// Page 5 · FIFO Report — morning-to-morning operating period
// ---------------------------------------------------------------------
function FifoReportPage({ data }) {
  const f = data.fifo || {};
  const products = f.products || [];
  const provisional = f.provisional_summary || {};
  const provInvoiceCount = provisional.invoice_count ?? 0;
  const provLitres = provisional.litre_count ?? 0;
  const hasProvisional = f.has_provisional === true || provInvoiceCount > 0;

  return (
    <PrintPage number={5} businessDate={data.meta?.business_date} totalPages={8}>
      <PackageHeader meta={data.meta} />
      <SectionHeader
        title="FIFO Inventory Report"
        subtitle={f.period_label
          ? `Report Date · ${data.meta?.business_date} · Operating Period · ${f.period_label}`
          : "FIFO layer position"}
      />

      {/* FIFO COST STATUS — single top-of-section summary, replaces per-row badges. */}
      <div
        className={`mt-3 rounded-md border px-3 py-2.5 ${
          hasProvisional
            ? "border-amber-300 bg-amber-50"
            : "border-emerald-300 bg-emerald-50"
        }`}
        data-testid="dp-fifo-cost-status"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">
              FIFO Cost Status
            </div>
            <div
              className={`mt-0.5 text-sm font-semibold ${
                hasProvisional ? "text-amber-900" : "text-emerald-900"
              }`}
              data-testid="dp-fifo-cost-status-label"
            >
              {hasProvisional ? "Provisional — freight pending" : "Final — all costs settled"}
            </div>
            {hasProvisional ? (
              <div className="mt-0.5 text-[11px] text-amber-900" data-testid="dp-fifo-cost-status-detail">
                {provInvoiceCount.toLocaleString()} invoice{provInvoiceCount === 1 ? "" : "s"} · {fmtLitres(provLitres)} awaiting freight allocation
              </div>
            ) : (
              <div className="mt-0.5 text-[11px] text-emerald-900">
                Every open layer has both fuel and freight invoices attached.
              </div>
            )}
          </div>
          <div className="text-right text-[10px] text-slate-600 leading-snug">
            <div><b>Report Date:</b> {fmtDate(f.report_date)}</div>
            <div><b>Operating Period:</b><br />{fmtDate(f.period_start)} AM &rarr; {fmtDate(f.period_end)} AM</div>
          </div>
        </div>
      </div>

      {/* Concise landed-cost basis note — presented once, no per-row repetition. */}
      <div
        className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-[10px] text-slate-600 leading-snug"
        data-testid="dp-fifo-cost-legend"
      >
        <b className="uppercase tracking-wider text-slate-700">Landed cost basis:</b>{" "}
        <b>Final</b> — supplier fuel + freight invoices both received.{" "}
        <b>Provisional</b> — supplier fuel approved, freight not yet received; displayed $/L
        reflects fuel cost only and flips to Final when the freight invoice is attached.
      </div>

      <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-700">Portfolio Summary</h3>
      <table className="w-full text-sm mt-2" data-testid="dp-fifo-summary">
        <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
          <tr>
            <th className="py-2 pr-2">Product</th>
            <th className="py-2 pr-2 text-right">Open Litres</th>
            <th className="py-2 pr-2 text-right">Wtd Avg Cost</th>
            <th className="py-2 pr-2 text-right">Next-Out Cost</th>
            <th className="py-2 pr-2 text-right">Inventory Value</th>
            <th className="py-2 pr-2 text-right">Period Purchases</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.product_code} className="border-b border-slate-100" data-testid={`dp-fifo-summary-${p.product_code}`}>
              <td className="py-1.5 pr-2 font-medium">{p.product_label}</td>
              <td className="py-1.5 pr-2 text-right font-mono" data-testid={`dp-fifo-summary-${p.product_code}-open-litres`}>{fmtLitres(p.total_open_litres)}</td>
              <td className="py-1.5 pr-2 text-right font-mono" data-testid={`dp-fifo-summary-${p.product_code}-wac`}>{fmtPriceL(p.weighted_avg_unit_cost)}</td>
              <td className="py-1.5 pr-2 text-right font-mono" data-testid={`dp-fifo-summary-${p.product_code}-next-out`}>{fmtPriceL(p.next_out_unit_cost)}</td>
              <td className="py-1.5 pr-2 text-right font-mono" data-testid={`dp-fifo-summary-${p.product_code}-value`}>{fmtMoney(p.total_open_value)}</td>
              <td className="py-1.5 pr-2 text-right font-mono" data-testid={`dp-fifo-summary-${p.product_code}-purchases`}>{fmtLitres(p.period_purchase_litres)}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-300 bg-slate-50 font-semibold" data-testid="dp-fifo-summary-portfolio">
            <td className="py-1.5 pr-2">Portfolio Total</td>
            <td className="py-1.5 pr-2 text-right font-mono">{fmtLitres(f.portfolio_open_litres)}</td>
            <td colSpan={2}></td>
            <td className="py-1.5 pr-2 text-right font-mono">{fmtMoney(f.portfolio_value)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {products.some((p) => (p.open_layers || []).length > 0) ? (
        <>
          <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-700">Open FIFO Layers (Oldest First)</h3>
          {products.filter((p) => (p.open_layers || []).length > 0).map((p) => (
            <div key={p.product_code} className="mt-2.5" data-testid={`dp-fifo-layers-${p.product_code}`}>
              <div className="text-[11px] font-semibold text-slate-800 mb-1">{p.product_label}</div>
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-1 pr-2">Supplier</th>
                    <th className="py-1 pr-2">Invoice</th>
                    <th className="py-1 pr-2">Delivery</th>
                    <th className="py-1 pr-2 text-right">Received</th>
                    <th className="py-1 pr-2 text-right">Remaining</th>
                    <th className="py-1 pr-2 text-right">Landed $/L</th>
                    <th className="py-1 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {p.open_layers.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1 pr-2">{l.supplier || "—"}</td>
                      <td className="py-1 pr-2 font-mono text-[10px]">{l.invoice_number || "—"}</td>
                      <td className="py-1 pr-2 font-mono text-[10px]">{l.delivery_date || "—"}</td>
                      <td className="py-1 pr-2 text-right font-mono">{fmtLitres(l.quantity_litres)}</td>
                      <td className="py-1 pr-2 text-right font-mono">{fmtLitres(l.residual_litres)}</td>
                      <td className="py-1 pr-2 text-right font-mono">{fmtPriceL(l.landed_unit_cost)}</td>
                      <td className="py-1 pr-2 text-[10px]">
                        {l.cost_final ? (
                          <span className="text-emerald-800 font-semibold" data-testid="dp-fifo-layer-status-final">Final</span>
                        ) : (
                          <span
                            className="text-amber-900 font-medium"
                            data-testid="dp-fifo-layer-status-pending"
                            title={l.provisional_reason || "Landed cost not yet finalised"}
                          >
                            Pending Freight
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      ) : (
        <EmptyState message="No open FIFO layers recorded for this business date." />
      )}
    </PrintPage>
  );
}


// ---------------------------------------------------------------------
// Page 8 · Auditor / Management Summary Snapshot
// ---------------------------------------------------------------------
function AuditSnapshotPage({ data }) {
  const a = data.audit_snapshot || {};
  const meta = data.meta || {};
  const rc = data.reconciliation || {};
  const review = a.review || {};
  const controls = a.controls || {};
  const managementReviewed = a.management_reviewed === true;

  // Banner tone is math-driven; the "reviewed & resolved" pill layers
  // on top so an auditor sees both the raw variance and whether
  // management has already reviewed it.
  const bannerCls = {
    green: "border-emerald-500 bg-emerald-50 text-emerald-900",
    amber: "border-amber-500 bg-amber-50 text-amber-900",
    red:   "border-rose-500 bg-rose-50 text-rose-900",
    slate: "border-slate-400 bg-slate-100 text-slate-700",
  }[a.banner_tone || "slate"];

  // Product row order mirrors the operator's DAILY INPUT worksheet.
  const PRODUCT_ORDER = ["regular", "premium", "clear_diesel", "dyed_diesel"];
  const PRODUCT_SHEET_LABEL = {
    regular:      "REGULAR GAS (RUL)",
    premium:      "PREMIUM GAS (PUL)",
    clear_diesel: "CLEAR DIESEL (ULS)",
    dyed_diesel:  "DYED DIESEL (ULSD)",
  };
  const items = (a.items || rc.items || []);
  const byCode = Object.fromEntries(items.map((it) => [it.product_code, it]));
  const controlsByProduct = controls.by_product || [];

  return (
    <PrintPage number={8} businessDate={meta.business_date} totalPages={8}>
      <PackageHeader meta={meta} />

      <div className="mt-4 flex items-baseline justify-between border-b-2 border-slate-900 pb-1">
        <h1 className="font-display text-lg font-bold uppercase tracking-wider text-slate-900">
          Coolman Fuels &middot; Daily Inventory Reconciliation
        </h1>
        <div className="text-[11px] font-mono text-slate-700">
          <b>Business Date:</b> {fmtDate(meta.business_date)}
        </div>
      </div>

      {/* Reconciliation status — math-driven; management-reviewed pill layered on top. */}
      <div className={`mt-4 rounded-md border-2 ${bannerCls} px-5 py-2.5`}
           data-testid={`dp-audit-banner-${a.banner_tone || "slate"}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[9px] font-semibold uppercase tracking-[0.25em]">Reconciliation Status</div>
            <div className="mt-0.5 font-display text-xl font-bold">{a.banner || "STATUS UNAVAILABLE"}</div>
          </div>
          {managementReviewed ? (
            <span
              className="self-center rounded-full border border-slate-800 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-800"
              data-testid="dp-audit-management-reviewed-pill"
            >
              Reviewed &amp; Resolved by Management
            </span>
          ) : (
            <span
              className="self-center rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
              data-testid="dp-audit-management-pending-pill"
            >
              Awaiting Management Review
            </span>
          )}
        </div>
      </div>

      {/* Product reconciliation table with three column groups:
          BULK PLANT · CARDLOCK · RECONCILIATION.
          Uses the authoritative engine outputs and daily-readings docs
          verbatim — no recomputation. */}
      {(() => {
        const tanks = (data.tank_inventory?.bulk_plant || []);
        const pumps = (data.daily_readings?.cardlock_pumps || []);
        const groups = PRODUCT_ORDER.map((code) => {
          const it = byCode[code] || {};
          const tanksForProduct = tanks.filter((t) => t.product === code);
          const pumpsForProduct = pumps.filter((p) => p.product === code);
          const rowCount = Math.max(tanksForProduct.length, pumpsForProduct.length, 1);
          const rows = [];
          for (let i = 0; i < rowCount; i++) {
            const t = tanksForProduct[i] || {};
            const p = pumpsForProduct[i] || {};
            const diff = (p.open != null && p.close != null && p.open !== "" && p.close !== "")
              ? String(Number(p.close) - Number(p.open))
              : (p.throughput != null ? String(p.throughput) : null);
            rows.push({ tank: t, pump: p, diff });
          }
          return { code, item: it, rows };
        });
        const st = (it) => it.status === "GREEN" ? "PASS"
          : it.status === "YELLOW" ? "REVIEW"
          : it.status === "RED" ? "ESCALATE" : "—";
        const stCls = (it) => it.status === "GREEN" ? "text-emerald-800 bg-emerald-50 border-emerald-300"
          : it.status === "YELLOW" ? "text-amber-900 bg-amber-50 border-amber-300"
          : it.status === "RED" ? "text-rose-900 bg-rose-50 border-rose-300"
          : "text-slate-700 bg-slate-50 border-slate-300";
        return (
          <table className="w-full text-[9px] mt-4 border-collapse" data-testid="dp-audit-recon-table">
            <thead className="border-b-2 border-slate-800">
              {/* Column group header row · Sprint 10 · 2026-08-13. */}
              <tr>
                <th className="py-1 px-1 border-b border-slate-400" rowSpan={2}>&nbsp;</th>
                <th
                  className="py-1.5 px-2 text-center text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800 border-r border-slate-400"
                  colSpan={4}
                  data-testid="dp-audit-group-bulk-plant"
                >
                  Bulk Plant
                </th>
                <th
                  className="py-1.5 px-2 text-center text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800 border-r border-slate-400"
                  colSpan={4}
                  data-testid="dp-audit-group-cardlock"
                >
                  Cardlock
                </th>
                <th
                  className="py-1.5 px-2 text-center text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800"
                  colSpan={6}
                  data-testid="dp-audit-group-reconciliation"
                >
                  Reconciliation
                </th>
              </tr>
              <tr className="align-bottom text-slate-700 border-b border-slate-400">
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Tank<br/>Capacity</th>
                <th className="py-1 px-1 text-left font-semibold uppercase tracking-wider">Dispenser</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Dispenser<br/>Closing</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider border-r border-slate-300">Closing<br/>Physical<br/>(Sum)</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Open</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Close</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Incoming<br/>Volume</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider border-r border-slate-300">RTS</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Cardlock<br/>Difference</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider" data-testid="dp-audit-th-throughput">Throughput</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Book<br/>Balance</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Daily<br/>Variance (L)</th>
                <th className="py-1 px-1 text-right font-semibold uppercase tracking-wider">Var %</th>
                <th className="py-1 px-1 text-center font-semibold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => g.rows.map((row, idx) => {
                const first = idx === 0;
                const rs = g.rows.length;
                const it = g.item;
                return (
                  <tr key={`${g.code}-${idx}`} className="border-b border-slate-200 align-top" data-testid={`dp-audit-row-${g.code}-${idx}`}>
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 font-semibold uppercase tracking-wider text-slate-800 border-r border-slate-300 align-top">
                        {PRODUCT_SHEET_LABEL[g.code]}
                      </td>
                    ) : null}
                    {/* BULK PLANT */}
                    <td className="py-1 px-1 text-right font-mono text-slate-600">{fmtLitres(row.tank.capacity_l)}</td>
                    <td className="py-1 px-1 text-slate-800">{row.pump.label || row.tank.tank_number || "—"}</td>
                    <td className="py-1 px-1 text-right font-mono">{fmtLitres(row.tank.closing_physical)}</td>
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono font-semibold border-r border-slate-300 bg-slate-50">{fmtLitres(it.physical_inventory)}</td>
                    ) : null}
                    {/* CARDLOCK */}
                    <td className="py-1 px-1 text-right font-mono text-slate-600">{fmtLitres(row.pump.open)}</td>
                    <td className="py-1 px-1 text-right font-mono text-slate-600">{fmtLitres(row.pump.close)}</td>
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono">{fmtLitres(it.deliveries)}</td>
                    ) : null}
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono border-r border-slate-300">{it.rts != null ? fmtLitres(it.rts) : "—"}</td>
                    ) : null}
                    {/* RECONCILIATION */}
                    <td className="py-1 px-1 text-right font-mono text-slate-600">{fmtLitres(row.diff)}</td>
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono">{fmtLitres(it.throughput_yesterday)}</td>
                    ) : null}
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono font-semibold">{fmtLitres(it.book_inventory_adjusted)}</td>
                    ) : null}
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono font-semibold">{fmtGL(it.diff_adjusted)}</td>
                    ) : null}
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-right font-mono">{fmtPct(it.variance_pct)}</td>
                    ) : null}
                    {first ? (
                      <td rowSpan={rs} className="py-1 px-1 text-center">
                        <span
                          className={`inline-block rounded border px-1.5 py-0.5 text-[8px] font-semibold uppercase ${stCls(it)}`}
                          data-testid={`dp-audit-status-${g.code}`}
                        >
                          {st(it)}
                        </span>
                      </td>
                    ) : null}
                  </tr>
                );
              }))}
            </tbody>
          </table>
        );
      })()}

      {/* Throughput Meter Aggregate — per product, split by Cardlock vs Loading Rack. */}
      <h3 className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-800 border-b border-slate-300 pb-1">
        Throughput Meter Aggregate (by Product)
      </h3>
      <table className="w-full text-[10px] mt-2 border-collapse" data-testid="dp-audit-meter-aggregate">
        <thead>
          <tr>
            <th className="py-1.5 px-1 text-left text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800 border-r border-slate-400" rowSpan={2}>Product</th>
            <th className="py-1.5 px-2 text-center text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800 border-r border-slate-400" colSpan={3}>Cardlock</th>
            <th className="py-1.5 px-2 text-center text-[10px] font-bold uppercase tracking-wider text-white bg-slate-800" colSpan={3}>Loading Rack</th>
          </tr>
          <tr className="text-slate-700 border-b border-slate-400 align-bottom">
            <th className="py-1 px-1 text-right uppercase tracking-wider">Open</th>
            <th className="py-1 px-1 text-right uppercase tracking-wider">Close</th>
            <th className="py-1 px-1 text-right uppercase tracking-wider border-r border-slate-300">Throughput</th>
            <th className="py-1 px-1 text-right uppercase tracking-wider">Open</th>
            <th className="py-1 px-1 text-right uppercase tracking-wider">Close</th>
            <th className="py-1 px-1 text-right uppercase tracking-wider">Throughput</th>
          </tr>
        </thead>
        <tbody>
          {controlsByProduct.length > 0 ? controlsByProduct.map((row) => (
            <tr key={row.product_code} className="border-b border-slate-100" data-testid={`dp-audit-meter-${row.product_code}`}>
              <td className="py-1 pr-1 font-semibold text-slate-800">{row.product_label}</td>
              <td className="py-1 px-1 text-right font-mono">{fmtLitres(row.cardlock?.open)}</td>
              <td className="py-1 px-1 text-right font-mono">{fmtLitres(row.cardlock?.close)}</td>
              <td className="py-1 px-1 text-right font-mono font-semibold border-r border-slate-300">{fmtLitres(row.cardlock?.throughput)}</td>
              <td className="py-1 px-1 text-right font-mono">{fmtLitres(row.loading_rack?.open)}</td>
              <td className="py-1 px-1 text-right font-mono">{fmtLitres(row.loading_rack?.close)}</td>
              <td className="py-1 px-1 text-right font-mono font-semibold">{fmtLitres(row.loading_rack?.throughput)}</td>
            </tr>
          )) : (
            <tr><td colSpan={7} className="py-2 text-center text-slate-500 italic">Meter aggregate not yet captured for this business date.</td></tr>
          )}
        </tbody>
      </table>

      {/* NOTE — verbatim reproduction of the worksheet's audit footer text. */}
      <div className="mt-4 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-[10px] text-slate-700 leading-snug"
           data-testid="dp-audit-worksheet-notes">
        <div className="font-semibold uppercase tracking-wider text-slate-700 mb-1">Note</div>
        <div className="italic mb-1">
          The Loading Rack Meters are NOT ATC&apos;d, therefore a variance on the overall Reconciliation is inevitable.
        </div>
        <div className="mb-1">
          <b>*</b> An investigation will take place if there is a discrepancy of
          5 consecutive losses, loss or gain 18 days/month, or 3 consecutive losses greater than 200 L per day,
          &plusmn;&thinsp;0.25% of throughput for Diesel and &plusmn;&thinsp;0.50% for Gasoline.
        </div>
        <div className="mb-1">
          Office Manager and/or Marketer will advise Territory Manager (WBM) in the event of a discrepancy.
          An Event Report will be completed accordingly per Event Reporting Procedures.
        </div>
        <div>
          <b>*</b> If a loss or gain occurs, investigate the following to determine the cause:
          &nbsp;Damaged or Shortened Dip Stick &middot; Inadequate Dipping Procedures &middot; Calculation Errors
          &middot; Inaccurate or Wrong Dip Charts &middot; Incorrect Meter Readings &middot; Short Deliveries
          &middot; Leaking Equipment &middot; Leaking Water Draw-Off Valves &middot; Product Theft &middot; Water Intrusion.
        </div>
      </div>

      {/* HISTORICAL INVESTIGATION CONTROLS — the four authoritative
          rules from the operator's DAILY INPUT workbook, evaluated by
          the single-source-of-truth engine in
          ``routes/investigation_controls.py``. Displayed compactly so
          the auditor can see, per product, whether any control has
          been triggered by today's row + the trailing history. */}
      {(() => {
        const ic = a.investigation_controls;
        if (!ic) return null;
        const products = Object.entries(ic.by_product || {});
        const CONTROL_KEYS = [
          { key: "daily_variance",             label: "Daily variance" },
          { key: "five_consecutive_losses",    label: "5 consecutive losses" },
          { key: "eighteen_loss_gain_days",    label: "18 loss/gain days" },
          { key: "three_consecutive_over_200", label: "3 consec. losses > 200 L" },
          { key: "thirty_day_cumulative_shortage", label: "30-day cum. shortage > 5%" },
        ];
        const PRODUCT_LABEL = {
          regular: "Regular Gasoline", premium: "Premium Gasoline",
          clear_diesel: "Clear Diesel", dyed_diesel: "Dyed Diesel",
        };
        return (
          <div className="mt-3 rounded border border-slate-300 bg-white px-3 py-2"
               data-testid="dp-audit-investigation-controls">
            <div className="flex items-center justify-between">
              <div className="font-semibold uppercase tracking-wider text-slate-700 text-[10px]">
                Historical Investigation Controls
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                ic.any_triggered
                  ? "border border-amber-300 bg-amber-50 text-amber-900"
                  : ic.any_incomplete
                    ? "border border-slate-300 bg-slate-50 text-slate-600"
                    : "border border-emerald-300 bg-emerald-50 text-emerald-800"
              }`} data-testid="dp-audit-controls-status">
                {ic.any_triggered ? "One or more triggered"
                  : ic.any_incomplete ? "Historical review incomplete"
                  : "None triggered"}
              </span>
            </div>
            <div className="mt-1 text-[9px] italic text-slate-500">{ic.policy}</div>
            <table className="mt-2 w-full text-[10px] border-collapse">
              <thead className="text-slate-600 border-b border-slate-300">
                <tr>
                  <th className="py-1 pr-1 text-left uppercase tracking-wider">Product</th>
                  {CONTROL_KEYS.map((c) => (
                    <th key={c.key} className="py-1 px-1 text-center uppercase tracking-wider">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(([code, block]) => (
                  <tr key={code} className="border-b border-slate-100" data-testid={`dp-audit-controls-${code}`}>
                    <td className="py-1 pr-1 font-medium text-slate-800">{PRODUCT_LABEL[code] || code}</td>
                    {CONTROL_KEYS.map((c) => {
                      const ctl = block[c.key] || {};
                      // Sprint 10 · Historical Inventory Controls · 2026-08-13.
                      // Distinguish TRIGGERED / CLEAR / INCOMPLETE. Missing
                      // evidence must NEVER read as "not triggered".
                      const status = ctl.status || (ctl.triggered ? "TRIGGERED" : "CLEAR");
                      const label = status === "TRIGGERED" ? "Triggered"
                                  : status === "INCOMPLETE" ? "Incomplete"
                                  : "Not triggered";
                      // Numeric summary — pick the most informative field.
                      const summary =
                        c.key === "daily_variance"          ? (ctl.variance_pct ? `${Number(ctl.variance_pct).toFixed(2)}%` : "n/a")
                      : c.key === "five_consecutive_losses" ? `${ctl.current_run}/${ctl.threshold}`
                      : c.key === "eighteen_loss_gain_days" ? `${ctl.count}/${ctl.threshold}`
                      : c.key === "three_consecutive_over_200" ? `${ctl.current_run}/${ctl.threshold}`
                      : c.key === "thirty_day_cumulative_shortage" ? (
                          ctl.shortage_pct ? `${Number(ctl.shortage_pct).toFixed(2)}% / ${Number(ctl.threshold_pct).toFixed(2)}%`
                          : `${ctl.history_days_available ?? 0}/${ctl.history_days_required ?? 30} days`
                        )
                      : "";
                      const tone = status === "TRIGGERED"
                        ? "border-amber-400 bg-amber-50 text-amber-900"
                        : status === "INCOMPLETE"
                          ? "border-slate-300 bg-slate-50 text-slate-600"
                          : "border-emerald-200 bg-emerald-50 text-emerald-800";
                      return (
                        <td key={c.key} className="py-1 px-1 text-center">
                          <div
                            className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${tone}`}
                            title={ctl.detail || ""}
                            data-testid={`dp-audit-controls-${code}-${c.key}-${status.toLowerCase()}`}
                          >
                            {label}
                          </div>
                          <div className="mt-0.5 font-mono text-[9px] text-slate-500">{summary}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* TRIGGERED TODAY — per-trigger reviewed/resolved pill. */}
      <div className={`mt-3 rounded border px-3 py-2 text-[10px] ${a.any_triggered ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
           data-testid={`dp-audit-triggers-${a.any_triggered ? "yes" : "no"}`}>
        <div className="flex items-center justify-between">
          <div className="font-semibold uppercase tracking-wider text-slate-700">Triggered Today</div>
          {a.any_triggered && managementReviewed ? (
            <span className="rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-800"
                  data-testid="dp-audit-triggers-reviewed">
              All Reviewed &amp; Resolved
            </span>
          ) : null}
        </div>
        {a.any_triggered ? (
          <ul className="mt-1 text-amber-900 space-y-1">
            {(a.triggers || []).map((t, i) => (
              <li key={i} className="flex items-start justify-between gap-3" data-testid={`dp-audit-trigger-${i}`}>
                <span><b>{t.criterion}</b> &mdash; {t.product_label}: {t.variance}</span>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider ${
                    t.reviewed
                      ? "border border-emerald-300 bg-white text-emerald-800"
                      : "border border-amber-300 bg-white text-amber-800"
                  }`}
                  data-testid={`dp-audit-trigger-${i}-status`}
                >
                  {t.reviewed ? "Reviewed" : "Detected"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-0.5 text-emerald-900">No investigation criteria triggered for this business date.</div>
        )}
      </div>

      {/* REASON FOR VARIANCE + MANAGEMENT REVIEW */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded border border-slate-300 px-3 py-2 text-[10px]" data-testid="dp-audit-reason">
          <div className="font-semibold uppercase tracking-wider text-slate-700">Reason for Variance</div>
          <div className="mt-1 italic text-slate-800 min-h-[42px] leading-snug">
            {review.investigation_notes ? `"${review.investigation_notes}"` : (
              <span className="not-italic text-slate-500">No variance reason recorded for this business date.</span>
            )}
          </div>
        </div>
        <div className="rounded border border-slate-300 px-3 py-2 text-[10px]" data-testid="dp-audit-review">
          <div className="font-semibold uppercase tracking-wider text-slate-700">Management Review</div>
          {managementReviewed ? (
            <div className="mt-1 text-slate-800 space-y-0.5">
              <div><b>Reviewed by:</b> {review.reviewed_by || "—"}</div>
              <div><b>Reviewed at:</b> {fmtDateTime(review.reviewed_at)}</div>
              <div><b>Overall assessment:</b> {review.overall_assessment || "—"}</div>
              {review.investigation_notes ? (
                <div className="pt-0.5"><b>Comments:</b> <span className="italic">{review.investigation_notes}</span></div>
              ) : null}
            </div>
          ) : (
            <div className="mt-1 text-amber-900">
              Not yet acknowledged. This snapshot was generated before the operator acknowledged the reconciliation.
            </div>
          )}
        </div>
      </div>

      {/* Footer metadata */}
      <dl className="mt-3 grid grid-cols-3 gap-x-4 gap-y-0.5 text-[10px] border-t border-slate-300 pt-2" data-testid="dp-audit-metadata">
        <div><dt className="inline text-slate-500">Business date:&nbsp;</dt><dd className="inline font-mono">{fmtDate(meta.business_date)}</dd></div>
        <div><dt className="inline text-slate-500">Report version:&nbsp;</dt><dd className="inline font-mono">{a.report_version || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Reconciliation status:&nbsp;</dt><dd className="inline font-mono">{a.overall_status || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Prepared by:&nbsp;</dt><dd className="inline font-mono">{meta.prepared_by_name || meta.prepared_by || "—"}</dd></div>
        <div><dt className="inline text-slate-500">Generated:&nbsp;</dt><dd className="inline font-mono">{fmtDateTime(meta.generated_at)}</dd></div>
        <div><dt className="inline text-slate-500">Source system:&nbsp;</dt><dd className="inline font-mono">{a.source_system || "—"}</dd></div>
      </dl>
    </PrintPage>
  );
}
